import { useEffect, useState } from 'react'

/** Either resolved color scheme. */
export type ColorScheme = 'dark' | 'light'

/**
 * Detect the system preference. When the system expresses *no* preference we
 * default to dark (festival-friendly).
 */
const detectSystem = (): ColorScheme => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light'
  return 'dark'
}

/** Apply/remove the `dark` class Tailwind keys off of. */
const applyScheme = (scheme: ColorScheme): void => {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', scheme === 'dark')
}

export interface UseColorScheme {
  scheme: ColorScheme
  isDark: boolean
}

/**
 * Color scheme is fully automatic: it follows the OS `prefers-color-scheme` with
 * no manual override. The pre-paint script in index.html sets the initial class;
 * this hook keeps it in sync if the system preference changes while the app is
 * open. Mount once near the app root.
 */
export const useColorScheme = (): UseColorScheme => {
  const [scheme, setScheme] = useState<ColorScheme>(detectSystem)

  useEffect(() => {
    applyScheme(scheme)
  }, [scheme])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setScheme(detectSystem())
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  return { scheme, isDark: scheme === 'dark' }
}
